import React, { ComponentProps } from 'react';
import { asFetchableBlock, useBlockContext } from '../../_lib/BlockFetcher';
import {
  BlockInteractions,
  Views as BlockInteractionsViews,
  DISPLAY_TYPE,
  createButtonObject,
} from '../../components/organisms/BlockInteractions';
import { Icons } from '../../components/atoms/SVG';
import { Namespace } from '../../translation/namespace';
import { DefaultView, EditView, OptionalView, Views } from '../../types/BlockWithViews';
import { BlockType } from '../../types/api/ElementType';
import { TicketBookingEditView } from './views/TicketBookingEditView';
import { TicketBookingDefaultView } from './views/TicketBookingDefaultView';
import { useContentWrapperContext } from '../../components/blocks/ContentWrapper/ContentWrapperContext';
import { useContentWrapperDndContext } from '../../components/blocks/ContentWrapper/ContentWrapperDndProvider';
import { useWidgetContext } from '../../_lib/components/Widget';
import { useSonTranslation } from '../../translation/useSonTranslation';
import { TicketBookingExportCsvView } from './views/TicketBookingExportCsvView';

type TicketBookingProps = Views<
  DefaultView<TicketBookingDefaultView> & EditView<TicketBookingEditView> & OptionalView<'exportAsCsv', any>
>;

function TicketBooking({ activeView, views }: TicketBookingProps) {
  const { t } = useSonTranslation('TicketBooking');
  const { uuid, elementType } = useBlockContext();
  // because of colons in uuid
  // const hashedUUID = murmurhash(uuid);
  const contentWrapperContext = useContentWrapperContext();
  const contentWrapperDndContext = useContentWrapperDndContext();
  const { header } = useWidgetContext();

  const blockInteractionButtons = () => {
    const exportCsvButton = createButtonObject(
      BlockInteractionsViews.EXPORT_AS_CSV,
      Icons.DOWNLOAD_ARROW,
      `${Namespace.TicketBooking}:buttons.export`,
      DISPLAY_TYPE.MODAL,
      <TicketBookingExportCsvView {...views.exportAsCsv} />,
      2
    );

    const editButton = createButtonObject(
      BlockInteractionsViews.EDIT,
      Icons.EDIT_PENCIL,
      `${Namespace.TicketBooking}:buttons.edit`,
      DISPLAY_TYPE.COLLAPSIBLE,
      <TicketBookingEditView {...views.edit} />
    );

    const deleteButton = createButtonObject(
      BlockInteractionsViews.DELETE,
      Icons.ERASER,
      `${Namespace.TicketBooking}:buttons.clear`,
      DISPLAY_TYPE.MODAL
    );

    return { ...exportCsvButton, ...editButton, ...deleteButton };
  };

  return (
    <BlockInteractions
      contentWrapperContext={contentWrapperContext}
      contentWrapperDndContext={contentWrapperDndContext}
      headline={{
        text: t('headline'),
        // if header is set, we already have a parent h2, so we use h3 here
        hierarchy: header ? 3 : 2,
        visible: true,
      }}
      alwaysShowInfo={false}
      views={views}
      activeView={activeView}
      buttons={blockInteractionButtons()}
      uuid={uuid}
      elementType={elementType}
    >
      <TicketBookingDefaultView {...views.default} />
    </BlockInteractions>
  );
}

export const TicketBookingBlock = asFetchableBlock(TicketBooking, BlockType.ticketbooking);
export type TicketBookingBlock = ComponentProps<typeof TicketBookingBlock>;
